import { TextField, type TextFieldProps } from '@mui/material';
import { ReactNode, useCallback, useMemo } from 'react';
import {
  Controller,
  ControllerRenderProps,
  FieldError,
  FieldValues,
  useFormContext,
} from 'react-hook-form';

export type RHFTextFieldProps = TextFieldProps & {
  name: string;
  errorIcon?: ReactNode;
  additionalOnChange?: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
};

export default function RHFTextField({
  name,
  helperText,
  type,
  errorIcon,
  FormHelperTextProps,
  defaultValue,
  additionalOnChange,
  ...other
}: RHFTextFieldProps) {
  const { control } = useFormContext();

  const hiddenProps: TextFieldProps = useMemo(() => {
    if (type !== 'hidden') return {};
    return {
      variant: 'standard',
      sx: {
        '& .MuiInputBase-root::before, & .MuiInputBase-root::after, & input': {
          display: 'none',
        },
      },
    };
  }, [type]);

  const handleChange = useCallback(
    (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
      field: ControllerRenderProps<FieldValues, string>
    ) => {
      additionalOnChange?.(event);
      if (type === 'number') {
        if (event.target.value !== '') {
          let val = Number(event.target.value);
          if (other.inputProps?.min && val < other.inputProps.min)
            val = other.inputProps.min;
          if (other.inputProps?.max && val > other.inputProps.max)
            val = other.inputProps.max;
          field.onChange(val);
        } else field.onChange(null);
      } else {
        // Add character limit validation for text fields
        const maxLength = other.inputProps?.maxLength;
        if (maxLength && event.target.value.length > maxLength) {
          return; // Don't update if exceeding max length
        }
        field.onChange(decodeURIComponent(event.target.value));
      }
    },
    [
      additionalOnChange,
      other.inputProps?.max,
      other.inputProps?.min,
      other.inputProps?.maxLength,
      type,
    ]
  );

  const helperTextMemoized = useCallback(
    (error?: FieldError) => {
      if (error)
        return (
          <>
            {errorIcon} {error?.message}
          </>
        );
      if (helperText) return helperText;
    },
    [errorIcon, helperText]
  );

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <TextField
          {...field}
          fullWidth
          type={type}
          defaultValue={defaultValue}
          value={
            type === 'number' && field.value === 0 ? '' : field.value ?? ''
          }
          onChange={(event) => handleChange(event, field)}
          error={!!error}
          helperText={helperTextMemoized(error)}
          FormHelperTextProps={FormHelperTextProps}
          InputLabelProps={{
            ...other.InputLabelProps,
            shrink: other.InputLabelProps?.shrink ?? Boolean(other.placeholder),
          }}
          {...other}
          {...hiddenProps}
          autoComplete="off"
        />
      )}
    />
  );
}
